import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Heading} from '../../components/guide/Heading';
import CtaBanner from '../../components/guide/CtaBanner';
import BoxImg1 from '../../assets/images/card/banner_box_img1.png';
import BoxImg2 from '../../assets/images/card/banner_box_img2.png';
import BoxImg3 from '../../assets/images/card/banner_box_img3.png';
import BoxImg4 from '../../assets/images/card/banner_box_img4.png';
import CfVideo from '../../assets/videos/cf_video.mp4';
import HeaderPath from '../../components/common/HeaderPath';
import BannerVideo1 from '../../assets/videos/banner_video_1.gif';
import BannerVideo2 from '../../assets/videos/banner_video_2.gif';
import BannerVideo3 from '../../assets/videos/banner_video_3.gif';
import BannerVideo4 from '../../assets/videos/banner_video_4.gif';

const BANNER_GIF_SLIDES = [
    {src: BannerVideo1, alt: '브랜딩 배너 미디어 아트 1'},
    {src: BannerVideo2, alt: '브랜딩 배너 미디어 아트 2'},
    {src: BannerVideo3, alt: '브랜딩 배너 미디어 아트 3'},
    {src: BannerVideo4, alt: '브랜딩 배너 미디어 아트 4'},
];

// Adjust the interval so it matches the playback length of each GIF.
const AUTO_SLIDE_INTERVAL_MS = 6500;
const DRAG_THRESHOLD_PX = 40;

const BannerContentPage: React.FC = () => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const dragStartXRef = useRef<number | null>(null);

    const slideCount = BANNER_GIF_SLIDES.length;

    const goToNextSlide = useCallback(() => {
        setCurrentSlide((prev) => (prev + 1) % slideCount);
    }, [slideCount]);

    const goToPrevSlide = useCallback(() => {
        setCurrentSlide((prev) => (prev - 1 + slideCount) % slideCount);
    }, [slideCount]);

    useEffect(() => {
        if (!isAutoPlaying) {
            return;
        }
        const timeoutId = window.setTimeout(goToNextSlide, AUTO_SLIDE_INTERVAL_MS);
        return () => window.clearTimeout(timeoutId);
    }, [currentSlide, goToNextSlide, isAutoPlaying]);

    const finishPointerInteraction = useCallback((clientX: number | null) => {
        if (dragStartXRef.current === null || clientX === null) {
            dragStartXRef.current = null;
            setIsAutoPlaying(true);
            return;
        }

        const deltaX = clientX - dragStartXRef.current;
        dragStartXRef.current = null;

        if (Math.abs(deltaX) > DRAG_THRESHOLD_PX) {
            if (deltaX < 0) {
                goToNextSlide();
            } else {
                goToPrevSlide();
            }
        }

        setIsAutoPlaying(true);
    }, [goToNextSlide, goToPrevSlide]);

    const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        dragStartXRef.current = event.clientX;
        setIsAutoPlaying(false);
    }, []);

    const handlePointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        finishPointerInteraction(event.clientX);
    }, [finishPointerInteraction]);

    const handlePointerLeave = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
        if (dragStartXRef.current !== null) {
            finishPointerInteraction(event.clientX);
        }
    }, [finishPointerInteraction]);

    const handlePointerCancel = useCallback(() => {
        dragStartXRef.current = null;
        setIsAutoPlaying(true);
    }, []);

    return (
        <section className="w-full bg-white">
            <div className="mx-auto w-full max-w-[1072px] px-0 sm:px-6 lg:px-0">
                <HeaderPath path={'> 콘텐츠 제작 > 배너 & 홈페이지 콘텐츠'}/>
                <Heading className="hidden lg:block lg:mt-[93px] lg:mb-[96px]" as="h1" size="h1">
                    배너 & 홈페이지 콘텐츠
                </Heading>
                <div className="mt-0 flex flex-col gap-12 sm:gap-16 lg:mt-0 lg:gap-[93px]">
                    <Heading as="p" size="subtitle" className="text-black font-[400]">
                        분양의신은 단순히 예쁜 디자인을 만드는 것에 그치지 않고, 광고 플랫폼별 AI로직에 최적화된 디자인의 홈페이지·배너를 제작합니다.
                    </Heading>

                    <div>
                        <h5 className="text-[20px] font-[700] text-black sm:text-[25px]">플랫폼별 최적화 디자인 배너</h5>
                        <div className="mt-5 flex items-center justify-center  bg-[#F1F3F5] py-10 sm:mt-[20px] sm:py-[73px]">
                            <img className="h-auto max-w-full" src={BoxImg1} alt="플랫폼별 최적화 배너 예시" />
                        </div>
                        <p className="mt-5 text-base font-[400] leading-relaxed text-slate-800 sm:mt-[20px] sm:text-[18px]">
                            유튜브, 구글, 카카오 등 광고플랫폼의 AI 알고리즘이 선호하는 디자인 원리를 연구하여, 각 사이즈별 최적화된 디자인으로 배너를 제작합니다.<br/>
                            단순히 시각적으로 예쁜 배너가 아닌, 노출·클릭·전환 성과를 극대화하는 형태로 설계합니다.
                        </p>
                    </div>

                    <div>
                        <h5 className="text-[20px] font-[700] text-black sm:text-[25px]">전환 유도형 모바일 홈페이지</h5>
                        <div className="mt-5 flex items-center justify-center  bg-[#F1F3F5] py-10 sm:mt-[20px] sm:py-[73px]">
                            <img className="h-auto max-w-full" src={BoxImg2} alt="전환 유도형 모바일 홈페이지 예시" />
                        </div>
                        <p className="mt-5 text-base font-[400] leading-relaxed text-slate-800 sm:mt-[20px] sm:text-[18px]">
                            사용자의 동선을 최소화하고, 즉각적인 콜·상담 연결이 가능하도록 설계합니다.<br/>
                            빠른 로딩 속도와 직관적 UI/UX를 통해 광고 퍼포먼스 극대화에 최적화되어 있습니다.
                        </p>
                    </div>

                    <div>
                        <h5 className="text-[20px] font-[700] text-black sm:text-[25px]">아트 디자인 기반 PC 브랜딩 홈페이지</h5>
                        <div className="mt-5  bg-[#F1F3F5] py-0 sm:mt-[20px] sm:flex sm:items-center sm:justify-center ">
                            <div className="relative flex flex-row md:gap-6 items-center justify-center">
                                <img
                                    className="w-[60%] md:w-[50%] h-auto"
                                    src={BoxImg3}
                                    alt="PC 브랜딩 홈페이지 레이아웃"
                                />
                                <div
                                    className="relative w-auto md:h-[237px] h-auto md:w-[354px] overflow-hidden rounded-md bg-white shadow-lg"
                                    style={{ aspectRatio: '354 / 237' }}
                                >
                                    <div
                                        className="flex h-full w-full transition-transform duration-700 ease-out"
                                        style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                                        onPointerDown={handlePointerDown}
                                        onPointerUp={handlePointerUp}
                                        onPointerLeave={handlePointerLeave}
                                        onPointerCancel={handlePointerCancel}
                                    >
                                        {BANNER_GIF_SLIDES.map((slide) => (
                                            <img
                                                key={slide.src}
                                                alt={slide.alt}
                                                src={slide.src}
                                                className="h-full w-full flex-shrink-0 select-none object-cover"
                                                draggable={false}
                                            />
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        className="hidden md:block absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1 text-white"
                                        onClick={goToPrevSlide}
                                        aria-label="이전 배너 보기"
                                    >
                                        &lt;
                                    </button>
                                    <button
                                        type="button"
                                        className="hidden md:block absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/50 p-1 text-white"
                                        onClick={goToNextSlide}
                                        aria-label="다음 배너 보기"
                                    >
                                        &gt;
                                    </button>
                                    <div className="gap-1">
                                        {BANNER_GIF_SLIDES.map((_, index) => (
                                            <span
                                                key={index}
                                                className={`h-[6px] w-[6px] rounded-full ${index === currentSlide ? 'bg-white' : 'bg-white/40'}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p className="mt-5 text-base font-[400] leading-relaxed text-slate-800 sm:mt-[20px] sm:text-[18px]">
                            단순한 랜딩이 아닌, 브랜드의 스토리와 신뢰를 전달하는 브랜딩 홈페이지를 제작합니다.<br/>
                            몰입도 높은 아트 디자인을 활용하여 사용자 경험을 극대화합니다.
                        </p>
                    </div>


                    <div>
                        <h5 className="text-[20px] font-[700] text-black sm:text-[25px]">미디어 아트 디자인 기반 CF 배너(영상) 제작</h5>
                        <div className="mt-5  bg-[#F1F3F5] py-10 sm:mt-[20px] sm:flex sm:items-center sm:justify-center sm:py-[73px]">
                            <div className="px-[20px] md:px-0 w-full flex items-center justify-center flex-row">
                                <img
                                    className="h-[113px] w-[97px] max-w-xs sm:h-[300px] sm:w-auto"
                                    src={BoxImg4}
                                    alt="미디어 아트 디자인 배너"
                                />
                                <div className={'flex-1 md:flex-none'}>
                                    <video
                                        className="w-full h-auto rounded-lg sm:h-[300px]"
                                        loop
                                        autoPlay
                                        controls={false}
                                        muted
                                        playsInline
                                        src={CfVideo}
                                    />
                                </div>
                            </div>
                        </div>
                        <p className="mt-5 text-base font-[400] leading-relaxed text-slate-800 sm:mt-[20px] sm:text-[18px]">
                            정적인 이미지를 넘어, 움직이는 영상 배너를 통해 시선을 더욱 강하게 끌어냅니다.
                            영상 배너는 특히 경쟁이 치열한 지면에서 광고 주목도를 높이는 핵심 수단으로 활용됩니다.
                        </p>
                    </div>

                    <div>
                    <h5 className="text-[20px] font-[700] text-black sm:text-[25px]">업계 최대 규모 디자인팀</h5>
                        <p className="mt-5 text-base font-[400] leading-relaxed text-slate-800 sm:mt-[20px] sm:text-[18px]">
                            분양의신은 업계에서 가장 많은 디자이너를 보유하고 있습니다.<br/>
                            단순 실행이 아닌 디자이너들의 적극적인 의견 제안과 지속적인 연구를 통해 플랫폼 로직을 분석하고,
                            끊임없이 성과를 개선하는 제작 문화를 유지합니다.
                        </p>
                    </div>


                    <CtaBanner
                        message="이런 홈페이지, 우리도 만들 수 있나요?"
                        buttonText="광고문의 바로가기 →"
                        href="https://분양의신.net/contact"
                    />
                </div>
            </div>
        </section>
    );
};

export default BannerContentPage;
